import React, { useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box/Box';
import TwInitialsIndicator from '../../TwInitialsIndicator/TwInitialsIndicator';
import StringUtils from '../../../utils/stringUtils';
import TwLoadMenu from '../../TwLoadMenu/TwLoadMenu';

import useAuth from '../../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import useFormatUrlsInText from '../../../hooks/useFormatUrlText';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { SplitButtonOption } from '../../TwButtons/SplitButton/SplitButton';
import useSegmentStatus from '../../../hooks/useSegmentStatus';
import { TwButton, TwButtonType, TwSplitButton } from '../../TwButtons/TwButtons';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import LoadUtils from '../../../utils/loadUtils';
import { LOAD_TYPE, LoadClient, LoadDocumentType } from '../../../redux/api/load/load.model';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TwAlert from '../../TwAlerts/TwAlerts';
import LoadsService from '../../../providers/Loads/Loads.service';
import TwWarningIcon from '../../TwWarningIcon/TwWarningIcon';
import TwScanner from '../../TwScanner/TwScanner';
import TwDispatchNotes from '../../TwDispatchNotes/TwDispatchNotes';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import useScrollToView from '../../../hooks/useScrollToView';
import TripOverview from './TripOverview';
import { TwLoadTooltipInfoProps } from '../../TwLoadTooltipInfo/TwLoadTooltipInfo';
import DateUtils from '../../../utils/dateUtils';
import { TabPanel } from '../../TwTabPanel/TwTabPanel';
import {
  Trip,
  TRIP_STATUS,
  TripStop,
  TRIPS_ITEMS_PER_PAGE
} from '../../../redux/api/trips/trips.model';
import {
  AttachmentBasicProps,
  ORDER_STATUS,
  Order,
  OrderAttachment
} from '../../../redux/api/orders/orders.model';
import TwUploadOrderTripFiles from '../../TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import {
  useDeleteOrderTripDocumentMutation,
  useUpdateOrderTripDocumentByIdMutation
} from '../../../redux/api/orders/ordersApi';
import TwDeleteOrderTripDoc from '../../TwUploadOrderTripFiles/TwDeleteOrderTripDoc';
import TwOrderTripsDocViewer from '../../TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import Typography from '@mui/material/Typography';
import useStopStatusProgression from '../../../hooks/useStopStatusProgression';
import TwEditDateTime from '../../TwEditDateTime/TwEditDateTime';
import {
  closeEditDateTime,
  setTripDetailReload
} from '../../../redux/features/navigation/navigationSlice';
import { tripApi, usePatchStopMutation } from '../../../redux/api/trips/tripApi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  TwLoadDetailTab,
  TwLoadDetailTabsGroup
} from '../../TwLoadDetail/components/LoadTabs/TwLoadDetailTabs';
import SegmentRouteInfo from '../../TwLoadDetail/components/SegmentRouteInfo/SegmentRouteInfo';
import DetailFile from '../../TwLoadDetail/DetailFile/DetailFile';
import EditFile from '../../TwLoadDetail/EditFiles/EditFiles';
import { TwLoadCommodityReadOnly } from '../../Commodities/TwLoadCommodities/TwLoadCommodities';
import { EntityType } from '../../../redux/Global.model';
import { useTripMenuOptions } from '../../../hooks/useLoadMenu';
import { EquipmentType } from '../../../redux/api/equipment/equipment.model';
import { TwEquipmentField } from '../../TwEquipmentField/TwEquipmentField';
import { TwSelectOption } from '../../TwSelects/TwSelects';
import FileUtils from '../../../utils/fileUtils';
import { useLocation } from 'react-router-dom';
import RTKUtils from '../../../utils/rtkUtils';
import TwExpensesTab from '../../TwExpensesTab/TwExpensesTab';
import TwDocumentsToGenerateTrip from '../../TwLoadDetail/components/DocumentsToGenerateTrip/TwDocumentsToGenerateTrip';
import TwPreviewDocument from '../../TwPreviewDocument/TwPreviewDocument';

export interface SegmentDetailProps {
  loadData: Order | null;
  segmentData: Trip;
  primaryClient: LoadClient | null;
  readonly?: boolean;
  forceOnlyEdit?: boolean;
  reloadData?: (entity?: 'trip' | 'order', tripId?: string) => void;
  segmentOrder: number;
  stops: TripStop[];
  open: boolean;
  changeTab: (event: React.SyntheticEvent, newValue: string, isClickFromTab: boolean) => void;
  onStatusChange?: () => void;
  handleMarkers: (stops: TripStop[], startOrder?: number) => void;
}

const isWebInFlutter = (): boolean => {
  return navigator.userAgent.includes('Flutter');
};

export const DROP_ZONE_HIGHLIGHTED_TEXT = 'Upload document';
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
const TripTab: React.FC<SegmentDetailProps> = ({
  loadData,
  segmentData,
  primaryClient,
  readonly,
  forceOnlyEdit,
  reloadData,
  segmentOrder,
  stops,
  open,
  changeTab,
  onStatusChange,
  handleMarkers
}): JSX.Element => {
  const route = useLocation();

  const forUrlsInText = useFormatUrlsInText();
  const { addNotification } = useNotifications();
  const [scrollIntoView] = useScrollToView();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const dispatch = useAppDispatch();
  const [updateOrderTripDocumentById] = useUpdateOrderTripDocumentByIdMutation();
  const [deleteOrderTripDocument] = useDeleteOrderTripDocumentMutation();
  const [patchStop] = usePatchStopMutation();

  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const dispatchNotesRef = React.useRef<HTMLDivElement>(null);
  const activeTrips = useAppSelector((state) => state.activeTrips);
  const isEnabledDriversSeeDocs = getTenantSetting('allow_drivers_to_view_documents');
  const isRequireBolActive = getTenantSetting('require-bol');
  const commoditiesEnabled = getTenantSetting('load-commodities');
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const driversEditEquipmentEnabled = getTenantSetting(
    'allow-drivers-to-edit-truck-and-trailer',
    false
  );
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { enableDynamicWebTwain, enableDocumentManager } = useFeatureFlags();

  const { statusOptions, updateStatus } = useSegmentStatus();
  const { progressStopStatusForStops } = useStopStatusProgression();
  const filters = useAppSelector((state) => state.activeTrips.filters);
  const isEditDateTimeOpen = useAppSelector((state) => state.navigation.isEditDateTimeOpen);
  const editDateTimeConfig = useAppSelector((state) => state.publicLoad.editDateTimeConfig);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const reloadTripDetail = useAppSelector((state) => state.navigation.tripDetailReload);
  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);

  const tripsFilterParams = useAppSelector((state) => state.activeTrips);

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;
  const isAdminOrDispatcher =
    userData?.role === USER_ROLE.ADMIN ||
    userData?.role === USER_ROLE.OWNER ||
    userData?.role === USER_ROLE.DISPATCHER;

  const [loading, setLoading] = useState<boolean>(false);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedAttachment, setSelectedAttachment] = useState<{
    attachmentId: string | null;
    documentAttachmentId: string | null;
  } | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<OrderAttachment>();
  const [fileUploadErrors, setFileUploadErrors] = useState<string[]>([]);
  const [documentsOpen, setDocumentsOpen] = useState<boolean>(false);
  const [docViewOpen, setDocViewOpen] = useState(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [segmentStops, setSegmentStops] = useState<TripStop[]>(stops);
  const [loadTabs, setLoadTabs] = useState<number>(0);
  const [matchesMobileOrFlutterApp, setMatchesMobileOrFlutterApp] = useState<boolean>(false);
  const [customersCount, setCustomersCount] = useState<number>(1);
  const [updateTripDetail, setUpdateTripDetail] = useState(false);

  // We get trip by id in case Trip tab is rendered in the Orders overlay
  const [tripData, setTripData] = useState<Trip | null>(segmentData);

  const segmentStatusSplitOptions = useMemo<SplitButtonOption[]>(() => {
    const newSplitOptions = statusOptions.map(
      (option): SplitButtonOption => ({
        text: option.label,
        onClick: (): void => {
          if (!loadData && !segmentData.id) return;
          setLoading(true);
          const prevStatus = segmentData.status || TRIP_STATUS.DRAFT;
          const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);

          option
            .setStatus(segmentData.id, prevStatus, matchesMobileOrTablet, tagsToInvalidate)
            .then(() => {
              reloadData && reloadData('order');
              dispatch(setTripDetailReload(true));
              setLoading(false);
              // Invalidate active trips to refresh the list
              // this is needed because the order status change could affect the trips
              // that are shown in the active trips list, so we need to refresh it since page 1 or offset 0
              if (route.pathname.includes('trips/board')) {
                dispatch(
                  tripApi.endpoints.getActivePersistentTrips.initiate({
                    filters: {
                      ...tripsFilterParams?.filters,
                      includeArchived: matchesMobileOrTablet,
                      status: [TRIP_STATUS.DISPATCHED]
                    },
                    params: {
                      sort_by: 'delivery_date',
                      order_by: 'asc',
                      limit: TRIPS_ITEMS_PER_PAGE,
                      offset: 0
                    }
                  })
                );
                dispatch(
                  tripApi.endpoints.getActivePersistentTrips.initiate({
                    filters: {
                      ...tripsFilterParams?.filters,
                      includeArchived: matchesMobileOrTablet,
                      status: [TRIP_STATUS.DISPATCHED]
                    },
                    params: {
                      sort_by: 'delivery_date',
                      order_by: 'asc',
                      limit: TRIPS_ITEMS_PER_PAGE,
                      offset: 0
                    }
                  })
                );
                dispatch(
                  tripApi.endpoints.getActivePersistentTrips.initiate({
                    filters: {
                      ...tripsFilterParams?.filters,
                      includeArchived: matchesMobileOrTablet,
                      status: [TRIP_STATUS.IN_TRANSIT]
                    },
                    params: {
                      sort_by: 'delivery_date',
                      order_by: 'asc',
                      limit: TRIPS_ITEMS_PER_PAGE,
                      offset: 0
                    }
                  })
                );
                dispatch(
                  tripApi.endpoints.getActivePersistentTrips.initiate({
                    filters: {
                      ...tripsFilterParams?.filters,
                      includeArchived: matchesMobileOrTablet,
                      status: [TRIP_STATUS.DELIVERED]
                    },
                    params: {
                      sort_by: 'delivery_date',
                      order_by: 'asc',
                      limit: TRIPS_ITEMS_PER_PAGE,
                      offset: 0
                    }
                  })
                );
              }
            })
            .catch((err) => {
              console.error(err);
              setLoading(false);
              addNotification({
                message: err.message,
                variant: 'error'
              });
            });
        }
      })
    );

    return newSplitOptions;
  }, [loadData]);

  const handleStopStatusProgressionFromDriver = async (): Promise<void> => {
    if (!segmentData.id) return;
    setLoading(true);
    const tags = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);
    progressStopStatusForStops(
      segmentData.id,
      segmentStops,
      segmentData.status || TRIP_STATUS.DRAFT,
      tags
    )
      .then(() => {
        setLoading(false);
        reloadData && reloadData();
        dispatch(setTripDetailReload(true));
      })
      .catch((err) => {
        setLoading(false);
        if (err.status === 409) {
          return addNotification({
            message: 'Stop was updated by another user. Please refresh the page and try again.',
            variant: 'error'
          });
        }
        addNotification({
          message: 'Failed at changing stop status, please try again',
          variant: 'error'
        });
      });
  };

  const handleStatusProgression = async (): Promise<void> => {
    if (!segmentData.id) return;
    if (segmentData?.status === TRIP_STATUS.DELIVERED) return;

    setLoading(true);

    const prevStatus = segmentData.status || TRIP_STATUS.DRAFT;
    const nextTripStatus = StringUtils.getNextTripStatus(segmentData?.status) || TRIP_STATUS.DRAFT;
    const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);

    updateStatus(
      segmentData.id,
      nextTripStatus,
      prevStatus,
      matchesMobileOrTablet,
      tagsToInvalidate
    )
      .then(() => {
        reloadData && reloadData();

        isDriver &&
          addNotification({
            message:
              nextTripStatus === TRIP_STATUS.DISPATCHED || nextTripStatus === TRIP_STATUS.IN_TRANSIT
                ? 'Load marked as picked up'
                : nextTripStatus === TRIP_STATUS.DELIVERED
                ? 'Load marked as delivered'
                : 'Load status updated',
            variant: 'success'
          });

        // Invalidate active trips to refresh the list
        // this is needed because the order status change could affect the trips
        // that are shown in the active trips list, so we need to refresh it since page 1 or offset 0
        if (route.pathname.includes('trips/board')) {
          dispatch(
            tripApi.endpoints.getActivePersistentTrips.initiate({
              filters: {
                ...tripsFilterParams?.filters,
                status: [TRIP_STATUS.DISPATCHED]
              },
              params: {
                sort_by: 'delivery_date',
                order_by: 'asc',
                limit: TRIPS_ITEMS_PER_PAGE,
                offset: 0
              }
            })
          );
          dispatch(
            tripApi.endpoints.getActivePersistentTrips.initiate({
              filters: {
                ...tripsFilterParams?.filters,
                status: [TRIP_STATUS.DISPATCHED]
              },
              params: {
                sort_by: 'delivery_date',
                order_by: 'asc',
                limit: TRIPS_ITEMS_PER_PAGE,
                offset: 0
              }
            })
          );
          dispatch(
            tripApi.endpoints.getActivePersistentTrips.initiate({
              filters: {
                ...tripsFilterParams?.filters,
                status: [TRIP_STATUS.IN_TRANSIT]
              },
              params: {
                sort_by: 'delivery_date',
                order_by: 'asc',
                limit: TRIPS_ITEMS_PER_PAGE,
                offset: 0
              }
            })
          );
          dispatch(
            tripApi.endpoints.getActivePersistentTrips.initiate({
              filters: {
                ...tripsFilterParams?.filters,
                status: [TRIP_STATUS.DELIVERED]
              },
              params: {
                sort_by: 'delivery_date',
                order_by: 'asc',
                limit: TRIPS_ITEMS_PER_PAGE,
                offset: 0
              }
            })
          );
        }
        onStatusChange && onStatusChange();
        setLoading(false);
      })
      .catch((err) => {
        console.error('updateStatus (trip) response did not returned 200');
        logRocketTrack('Status update', {
          action: 'Change stop status',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        setLoading(false);
        addNotification({
          message: 'Failed at changing trip status, please try again',
          variant: 'error'
        });
      });
  };

  const isDocumentVisible = (
    docVisibility: boolean | undefined,
    type: LoadDocumentType
  ): boolean => {
    if (type === LoadDocumentType.tripManifest) {
      return isDriver ? docVisibility || false : true;
    }

    return !isDriver || docVisibility || (docVisibility === undefined && isEnabledDriversSeeDocs);
  };

  const handleDocViewerOpen = (
    isDocVisible: boolean | undefined,
    docId: string,
    type: LoadDocumentType,
    attachmentId: string | null,
    documentAttachmentId: string | null
  ): void => {
    if (isDocumentVisible(isDocVisible, type)) {
      setDocViewOpen(true);
      setSelectedDocumentId(docId);
      setSelectedAttachment({ attachmentId, documentAttachmentId });
    }
  };

  const handleDocumentsUpload = async (): Promise<void> => {
    isTwScannerOpen && setIsTwScannerOpen(false);
    reloadData && reloadData();
  };

  const handleCloseEdit = (): void => {
    setShowEdit(false);
    setSelectedDocument(undefined);
  };

  const handleEditSuccess = (): void => {
    handleCloseEdit();
    reloadData && reloadData();
  };

  useEffect(() => {
    setMatchesMobileOrFlutterApp(matchesMobileOrTablet || isWebInFlutter());
  }, []);

  useEffect(() => {
    if (!open) {
      setTripData(null);
    }
  }, [open]);

  useEffect(() => {
    if (isTripDetailOpen) {
      handleMarkers(segmentData.stops, segmentData.sort_order);
    }
  }, [segmentData]);

  useEffect(() => {
    if (stops.length > 0) {
      const segmentStops = stops.filter((stop) => stop.trip_id === segmentData.id);

      // TODO Check this as GET TRIP responds with trip_sort_order
      const prevSegmentLastStop = stops.find(
        (stop) => stop.sort_order === segmentStops[0]?.sort_order - 1
      );

      if (prevSegmentLastStop) {
        setSegmentStops([prevSegmentLastStop, ...segmentStops]);
      } else {
        setSegmentStops(segmentStops);
      }
    }
  }, [stops]);

  useEffect(() => {
    if (loadTabs === 1 && matchesMobileOrTablet) {
      scrollIntoView(dispatchNotesRef, 'start');
    }
  }, [loadTabs, matchesMobileOrTablet]);

  useEffect(() => {
    // Getting trip data from the trip id to get extra data we need
    (async (): Promise<void> => {
      if ((segmentData.id && isLoadDetailOpen) || (reloadTripDetail && isLoadDetailOpen)) {
        setLoading(true);
        try {
          const trip = await LoadsService.getTripById(segmentData.id);

          setTripData(trip);

          dispatch(setTripDetailReload(false));
          setLoading(false);
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [segmentData.id, isLoadDetailOpen, reloadTripDetail]);

  useEffect(() => {
    if (tripData?.stops) {
      handleMarkers(tripData.stops || [], segmentStops[0]?.sort_order);
    }
  }, [tripData?.stops, segmentStops]);

  const handleDeleteDoc = (): void => {
    if (selectedDocument) {
      deleteOrderTripDocument({
        id: selectedDocument.id,
        orderId: loadData?.id,
        tripId: segmentData?.id,
        searchTripParams: activeTrips,
        status: segmentData?.status
      })
        .unwrap()
        .then(() => {
          setShowDelete(false);
          setSelectedDocument(undefined);

          if (isTripDetailOpen || updateTripDetail) {
            dispatch(setTripDetailReload(true));
            setUpdateTripDetail(false);
          } else {
            reloadData && reloadData();
          }
        })
        .catch((e) => {
          addNotification({
            variant: 'error',
            message: e.message || 'Error deleting document'
          });
          setShowDelete(false);
          setSelectedDocument(undefined);
        });
    }
  };

  const handleDownloadDoc = async (doc: OrderAttachment): Promise<void> => {
    addNotification({
      message: 'Downloading...',
      variant: 'loading',
      timeToShow: 20000
    });

    if (doc?.attachment?.s3_signed_download_url && isWebInFlutter()) {
      FileUtils.fetchAndDownloadFileLinkClick(doc.attachment.s3_signed_download_url);
      addNotification({
        message: 'Download complete',
        variant: 'success'
      });
    } else {
      try {
        const docSrc = await LoadsService.getLoadFile(doc.id);
        const docLink = URL.createObjectURL(docSrc);
        const docName = doc && 'attachment' in doc ? doc.attachment.name : null;
        const docKey = doc && 'attachment' in doc ? doc.attachment.key : null;

        const isOrderAttachment = doc.type === LoadDocumentType.billOfLading;
        const prefix = isOrderAttachment ? 'Load' : 'Trip';
        const readableId = isOrderAttachment ? loadData?.readable_id : segmentData?.readable_id;

        const link = document.createElement('a');
        link.href = docLink || '';
        link.download = docName
          ? `${prefix}#${readableId}-${docName}`
          : docKey
          ? `${prefix}#${readableId}-${docKey}`
          : `${prefix}#${readableId}-attachment.pdf`;

        link.click();
        setTimeout(function () {
          // For Firefox it is necessary to delay revoking the ObjectURL
          docLink && window.URL.revokeObjectURL(docLink);
        }, 100);
        addNotification({
          message: 'Download complete',
          variant: 'success'
        });
      } catch {
        addNotification({
          message: "Couldn't download attachment",
          variant: 'error'
        });
      }
    }
  };

  const handleCloseDeleteDoc = (): void => {
    setShowDelete(false);
    setSelectedDocument(undefined);
  };

  const handleChangeUploadDocument = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e?.target?.files !== null) {
      if (e.target.files[0].size > MAX_FILE_SIZE) {
        addNotification({
          message: 'Error: maximum attachment size 15 mb',
          variant: 'error',
          position: 'bottomCenter'
        });
        return;
      }
      //  setFileToUpload(e.target.files[0]);
      e.target.value = '';
      setDocumentsOpen(true);
    }
  };
  const closeScanner = (documentUploaded: boolean, showNotification = true): void => {
    setIsTwScannerOpen(false);

    if (!documentUploaded) {
      logRocketTrack('Upload', {
        action: 'Upload document scanner on trip tab',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      if (showNotification) {
        addNotification({
          message: 'No document uploaded',
          position: 'bottomCenter',
          variant: 'error'
        });
      }
    }
  };

  const handleLoadDetailsTabChange = (_event: React.SyntheticEvent, newValue: number): any => {
    setLoadTabs(newValue);
  };

  const handleSetHiddenFromDriver = (
    doc: OrderAttachment,
    driverVisibility: boolean,
    updateTripDetail?: boolean
  ): void => {
    if (doc.id) {
      updateOrderTripDocumentById({
        data: {
          visibility: driverVisibility,
          type: doc.type,
          name: doc.attachment.name,
          tripId: doc.trip_id,
          orderId: doc.order_id
        },
        id: doc.id
      })
        .unwrap()
        .then(() => {
          reloadData && reloadData();
          updateTripDetail && dispatch(setTripDetailReload(true));
        })
        .catch((e: any) => {
          addNotification({
            variant: 'error',
            message: e.message || 'Error updating document visibility'
          });
        });
    }
  };

  const editDateTime = async (ISODate: string): Promise<void> => {
    if (!segmentData) return;

    const { load_stop_id, type, segment_id } = editDateTimeConfig;
    const tagsToInvalidate = RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);

    const params: any = {
      stopId: load_stop_id,
      body: { trip_id: segment_id },
      searchTripParams: { filters },
      invalidateTags: tagsToInvalidate
    };
    if (type === 'in') params.body.in_time = ISODate;
    if (type === 'out') params.body.out_time = ISODate;

    setLoading(true);

    patchStop(params)
      .unwrap()
      .then(() => {
        addNotification({
          message: `Stop ${editDateTimeConfig.type} time updated successfully`,
          variant: 'success'
        });
        dispatch(closeEditDateTime());
        reloadData && reloadData();
        dispatch(setTripDetailReload(true));
      })
      .catch(() => {
        addNotification({
          message: `Something went wrong. Please try again.`,
          variant: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const statusLabelButton = useMemo(() => {
    if (segmentStops.length > 0) {
      return LoadUtils.getNextStopLevelStatusLabel(segmentStops) || '';
    } else {
      return '';
    }
  }, [segmentStops]);

  const showOrderTag = (orderId: string | number): string | number => {
    if (!tripData) return '';
    const order = tripData.orders?.find((o) => o.id === orderId);
    return order ? order.readable_id : '';
  };

  // Return an array of tooltip data for all the orders related to the Trip
  const ordersTooltipDataArray = useMemo(() => {
    if (!segmentData && !tripData) return [];

    // If we are in Orders overlay, we have to use tripData to get the trip full orders data
    const tripOrders = isLoadDetailOpen ? tripData?.orders || [] : segmentData.orders || [];

    return LoadUtils.getOrderTooltipInfo(tripOrders, use24HourClock) ?? [];
  }, [segmentData, tripData?.orders, isLoadDetailOpen, use24HourClock]);

  const orderOptions = useMemo(() => {
    let options = [] as any[];

    if (tripData && tripData.orders && tripData.orders.length > 0) {
      options = tripData.orders.map((o) => {
        return {
          value: o.id,
          label: o.readable_id as unknown as string,
          customer: o.raw_customer?.name || '',
          readableId: o.readable_id,
          customerOrderNumber: o.customer_order_number
        };
      });
    }

    options.push({
      value: '',
      label: '-',
      customer: 'Do not assign order'
    });

    return options;
  }, [tripData?.orders]);

  const tripAttachments = useMemo(() => {
    if (enableLtl) {
      return (tripData?.trip_attachments || []).filter((doc) =>
        isDocumentVisible(doc.visibility, doc.type)
      );
    } else {
      const orderAttachments =
        loadData?.order_attachments?.filter((doc) => {
          if ((doc.trip_id && doc.trip_id === segmentData.id) || !doc.trip_id) {
            return doc;
          }
        }) || [];

      const hasSegmentDataTripAttachments =
        segmentData.trip_attachments && segmentData.trip_attachments.length > 0;
      const tripAttachments = hasSegmentDataTripAttachments
        ? segmentData.trip_attachments
        : tripData?.trip_attachments || [];

      const attachments = [...orderAttachments, ...tripAttachments];

      // in case duplicates, remove them
      const uniqueAttachments = attachments.filter(
        (doc, index, self) => index === self.findIndex((t) => t.id === doc.id)
      );

      return uniqueAttachments.filter((doc) => isDocumentVisible(doc.visibility, doc.type));
    }
  }, [tripData?.trip_attachments, enableLtl, loadData?.order_attachments]);

  const getFilteredTripAttachments = useMemo((): OrderAttachment[] => {
    return tripAttachments?.filter((doc) => doc.type !== LoadDocumentType.tripManifest) || [];
  }, [tripAttachments]);

  const filesToPreview = useMemo((): OrderAttachment[] => {
    let filespreview: OrderAttachment[] = [];

    if (isDriver) {
      const docsfiltered = tripAttachments?.filter((doc) => doc.visibility);
      filespreview = [...(docsfiltered ?? [])];
    } else {
      filespreview = [...(tripAttachments ?? [])];
    }

    return filespreview;
  }, [isDriver, tripAttachments]);

  useEffect(() => {
    // Compare each client id inside each order in a trip to check how many unique clients are there
    // This information will be used to display the number of clients in the Trip overlay (main tab)
    const uniqueClients = new Set(segmentData.orders?.map((order) => order.raw_customer?.id));
    const uniqueClientsCount = uniqueClients.size;

    if (uniqueClientsCount > 1) {
      setCustomersCount(uniqueClientsCount);
    }
  }, [segmentData.orders]);

  const tripMenuOptions = useTripMenuOptions(tripData);

  const orderType = useMemo(() => {
    if (enableLtl) {
      return LOAD_TYPE.SEGMENT;
    } else {
      if (loadData) {
        return loadData.order_type;
      } else {
        return LOAD_TYPE.SEGMENT;
      }
    }
  }, [enableLtl, loadData]);

  const showCommodities = useMemo(() => {
    if (!commoditiesEnabled) return false;

    if (enableLtl && segmentData) {
      if (segmentData.trip_commodities) return segmentData.trip_commodities.length > 0;
      if (tripData?.trip_commodities) return tripData.trip_commodities.length > 0;

      return false;
    }

    return loadData && loadData.order_commodities && loadData.order_commodities.length > 0;
  }, [commoditiesEnabled, segmentData, loadData, tripData]);

  const getCommodities = (): any => {
    if (enableLtl && segmentData) {
      return segmentData.trip_commodities
        ? segmentData.trip_commodities
        : tripData?.trip_commodities
        ? tripData.trip_commodities
        : [];
    }

    return loadData?.order_commodities;
  };

  const readableTripNumber = useMemo((): string => {
    const orderReadableId = loadData
      ? loadData.readable_id
      : segmentData.order_readable_id || undefined;
    const tripOrder = segmentData.sort_order;
    const tripReadableId = segmentData.readable_id;

    return !enableLtl && orderReadableId
      ? `${orderReadableId}-${tripOrder + 1}`
      : tripReadableId
      ? `${tripReadableId}`
      : '';
  }, [segmentData, loadData]);

  const updateCounterExpenseTab = (): void => {
    reloadData && reloadData();
  };

  return (
    <>
      <Box component="section" className="load-detail__sub-header">
        <Box component="div" className="load-detail__sub-header__user-info">
          {primaryClient && (
            <>
              <TwInitialsIndicator
                text={(primaryClient?.initials ?? primaryClient?.name) || ''}
                displayRawText={!primaryClient?.initials}
              />
              <Box
                component="div"
                className="load-detail__sub-header__user-info--data"
                sx={{
                  justifyContent: 'center'
                }}
              >
                <Box component="span">
                  {primaryClient?.name}
                  {customersCount > 1 && `, +${customersCount - 1}`}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box component="div" className="load-detail__sub-header__btn-container">
          {/* Driver role call to action */}
          {isDriverOrCarrier &&
            segmentData &&
            LoadUtils.getNextStopLevelStatusLabel(segmentStops) &&
            !loading && (
              <TwButton
                type={TwButtonType.primary}
                label={statusLabelButton}
                action={handleStopStatusProgressionFromDriver}
                fullwidth={matchesMobileOrTablet}
              />
            )}

          {
            /* Admin roles action button */
            !isDriverOrCarrier && !isCustomerContact && (!readonly || !forceOnlyEdit) && (
              <TwSplitButton
                disabled={loading}
                label={LoadUtils.getTripStatusReadableText(
                  segmentData?.status || TRIP_STATUS.DRAFT
                )}
                options={segmentStatusSplitOptions}
                onIconButtonClick={handleStatusProgression}
              />
            )
          }

          {!isDriverOrCarrier && (!readonly || forceOnlyEdit) && (
            <Box component="div" className="load-detail__sub-header__btn-container--menu-container">
              <TwLoadMenu
                options={forceOnlyEdit ? [tripMenuOptions[0]] : tripMenuOptions}
                paperClassName="load-menu"
                buttonClassName="load-menu-btn"
              />
            </Box>
          )}
        </Box>
      </Box>
      {matchesMobileOrTablet && !isCustomerContact && (
        <Box component="section" className="sub-header">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {matchesMobileOrTablet &&
              (enableDynamicWebTwain ? (
                <TwButton
                  type={TwButtonType.secondary}
                  label={'Upload docs or photos'}
                  action={(): void => setIsTwScannerOpen(true)}
                  fullwidth={matchesMobileOrTablet}
                  className={'dynamicwt-button'}
                />
              ) : (
                <>
                  <input
                    type="file"
                    id="upload"
                    accept="application/pdf image/png image/jpeg image/heic"
                    hidden
                    onChange={handleChangeUploadDocument}
                  />

                  <Typography
                    component={'label'}
                    htmlFor="upload"
                    className="load-detail__mobile-upload-btn"
                  >
                    Upload document
                  </Typography>
                </>
              ))}
          </Box>
        </Box>
      )}

      <Box component="section" className="load-detail__data-container">
        <Box component="div" className="load-detail__data-container__data">
          <TwLoadDetailTabsGroup
            value={loadTabs}
            onChange={handleLoadDetailsTabChange}
            aria-label="TwTabs"
            role="TwTabs"
          >
            <TwLoadDetailTab label="Trip overview" />
            <TwLoadDetailTab
              label="Expenses"
              icon={
                <Chip
                  sx={{ height: '20px' }}
                  label={segmentData.other_expenses_count_by_role || 0}
                />
              }
              iconPosition="end"
            />
            {!isCustomerContact && (
              <TwLoadDetailTab
                label="Notes"
                icon={<Chip sx={{ height: '20px' }} label={segmentData.dispatch_note_count || 0} />}
                iconPosition="end"
              />
            )}
          </TwLoadDetailTabsGroup>
          <TabPanel id="load-detail-tab" value={loadTabs} index={0}>
            <>
              <Box
                component="div"
                className="load-detail__data-container__data--title"
                sx={{ marginBottom: '10px' }}
              >
                Route
              </Box>
              {tripData && (
                <SegmentRouteInfo
                  allSegments={loadData ? loadData?.trips : [segmentData]}
                  segment={isLoadDetailOpen ? tripData : segmentData}
                  startOrder={segmentStops[0]?.sort_order || 1}
                  ordersTooltipDataArray={ordersTooltipDataArray}
                  loadId={loadData?.id || ''}
                  showStatusUpdateInput={!isDriverOrCarrier && !isCustomerContact}
                  showAddSignatureAction={!isCustomerContact}
                  reloadData={reloadData}
                  isCustomerLink={isCustomerContact}
                />
              )}

              {showCommodities && (
                <TwLoadCommodityReadOnly
                  commodities={getCommodities()}
                  entity={enableLtl ? EntityType.TRIP : EntityType.ORDER}
                  ordersTooltipDataArray={ordersTooltipDataArray}
                  overlayView
                  changeTab={changeTab}
                />
              )}

              {!isCustomerContact && (
                <>
                  <Box component="div" className="load-detail__data-container__data--title">
                    Trip notes
                  </Box>
                  <Box
                    component="div"
                    className="load-detail__data-container__data__special-instructions"
                  >
                    {segmentData && forUrlsInText(segmentData.instructions || '')}
                  </Box>
                </>
              )}

              <Box component="div" className="load-detail__data-container__data--title">
                Equipment
              </Box>
              <Box component="div" className="load-detail__data-container__data__equipment">
                <TwEquipmentField
                  driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                  equipmentType={EquipmentType.truck}
                  userRole={userData?.role}
                  segmentId={segmentData.id}
                  equipmentId={segmentData.truck_id}
                />
                <TwEquipmentField
                  driversEditEquipmentEnabled={driversEditEquipmentEnabled}
                  equipmentType={EquipmentType.trailer}
                  userRole={userData?.role}
                  segmentId={segmentData.id}
                  equipmentId={segmentData.trailer_id}
                />
              </Box>

              {!isCustomerContact && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box component="div" className="load-detail__data-container__data--title">
                      Attachments
                    </Box>
                    {isRequireBolActive &&
                      !matchesMobileOrTablet &&
                      segmentData &&
                      segmentData.bol_missing && <TwWarningIcon message="POD is missing" />}
                  </Stack>

                  <Box component="div" className="load-detail__data-container__data__attachments">
                    <Stack
                      className="load-detail__data-container__data__attachments--container-2"
                      onClick={(): void => {
                        if (matchesMobileOrTablet && enableDynamicWebTwain) {
                          setIsTwScannerOpen(true);
                        } else {
                          setDocumentsOpen(true);
                        }
                      }}
                    >
                      <p className="load-detail__data-container__data__attachments--text">
                        <CloudUploadIcon
                          className="load-detail__data-container__data__attachments--icon"
                          sx={{ marginRight: '10px' }}
                        />
                        Click here to{' '}
                        <span className="load-detail__data-container__data__attachments--highlited-text">
                          upload a file
                        </span>
                      </p>
                      <p className="load-detail__data-container__data__attachments--secondaryText">
                        Files Supported: PDF, XSLS, JPG, PNG, maximum size 200MB{' '}
                      </p>
                    </Stack>

                    {fileUploadErrors.length > 0 &&
                      fileUploadErrors.map((error, i) => <TwAlert key={i} message={error} />)}

                    {getFilteredTripAttachments.map((document) => (
                      <DetailFile
                        key={document.id}
                        onClick={(): void => {
                          handleDocViewerOpen(
                            document.visibility,
                            document.id,
                            document.type,
                            document.attachment_id,
                            document.document_attachment_id
                          );
                        }}
                        fileName={document.attachment.name}
                        type={StringUtils.getLoadDocumentTypeLabel(document.type)}
                        created_at={document.created_at ? new Date(document.created_at) : undefined}
                        onEdit={(): void => {
                          setShowEdit(true);
                          setSelectedDocument(document);
                        }}
                        onDelete={(): void => {
                          setShowDelete(true);
                          setSelectedDocument(document);
                        }}
                        onDownload={(): void => {
                          handleDownloadDoc(document);
                        }}
                        showMenu={isDriverOrCarrier ? false : true}
                        driverVisibility={document.visibility}
                        size={document.attachment.size}
                        onVisibilityMenu={(visible: boolean): void => {
                          handleSetHiddenFromDriver(document, visible, true);
                        }}
                        showVisibility={true}
                        isEncrypted={document.is_encrypted}
                        fileKey={document.attachment.key}
                        fileType={document.attachment.file_type}
                        tag={
                          document.order_id
                            ? `Order #${showOrderTag(document.order_id)}`
                            : undefined
                        }
                        attachmentId={document.id}
                      />
                    ))}
                  </Box>
                </>
              )}

              <TwDocumentsToGenerateTrip
                files={tripAttachments}
                tripId={segmentData.id}
                isDriverOrCarrier={isDriverOrCarrier}
                onGenerate={(): void => {
                  dispatch(setTripDetailReload(true));
                }}
                onClick={(id, type): void => {
                  handleDocViewerOpen(true, id, type, null, null);
                }}
                onDelete={(file): void => {
                  if (file) {
                    setShowDelete(true);
                    setSelectedDocument(file);
                    setUpdateTripDetail(true);
                  }
                }}
                onDownload={(file): void => {
                  handleDownloadDoc(file);
                }}
              />
            </>
          </TabPanel>
          <TabPanel id="expenses-tab" value={loadTabs} index={1}>
            <TwExpensesTab
              tripId={segmentData.id || ''}
              readableTripId={readableTripNumber}
              updateCounterTab={updateCounterExpenseTab}
            />
          </TabPanel>
          <TabPanel id="dispatch-notes-tab" value={loadTabs} index={2}>
            <TwDispatchNotes
              orderId={loadData?.id || ''}
              tripId={segmentData.id || ''}
              isSplitLoad={true}
              reloadData={reloadData}
              loadReadableId={loadData?.readable_id || 0}
              componentRef={dispatchNotesRef}
              ltlEnabled={enableLtl}
            />
          </TabPanel>
        </Box>

        {(matchesMobileOrTablet && loadTabs === 2) || loadTabs === 1 ? (
          <></>
        ) : (
          <TripOverview
            segmentData={isLoadDetailOpen && tripData ? tripData : segmentData}
            tripOrders={tripData?.orders || []}
            readableId={
              !enableLtl
                ? loadData?.readable_id || tripData?.readable_id
                : segmentData.readable_id || tripData?.readable_id
            }
            ordersTooltipDataArray={ordersTooltipDataArray}
            changeTab={changeTab}
            orderType={orderType}
            reloadData={reloadData}
          />
        )}
      </Box>

      {loading && (
        <Backdrop
          className="load-detail__trip-loader"
          sx={{
            color: '#fff',
            position: 'absolute',
            zIndex: 10000,
            top: 0,
            right: 0
          }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <TwUploadOrderTripFiles
        open={documentsOpen}
        orderId={loadData?.id || ''}
        tripId={segmentData.id || ''}
        entity="trip"
        defaultVisibility={isDriver ? true : isEnabledDriversSeeDocs}
        handleClose={(): void => setDocumentsOpen(false)}
        entityList={orderOptions}
        onAddDocumentsSuccess={reloadData}
        disableInternalUpload={false}
      />

      <TwDeleteOrderTripDoc
        open={showDelete}
        fileName={selectedDocument?.attachment.name || ''}
        onClickClose={handleCloseDeleteDoc}
        onDelete={handleDeleteDoc}
      />

      <EditFile
        open={showEdit}
        entity="trip"
        document={selectedDocument}
        onClickCLose={handleCloseEdit}
        onEditSuccess={handleEditSuccess}
        entityList={orderOptions}
        order={{ id: loadData?.id || '', readable_id: loadData?.readable_id || '' }}
      />

      {matchesMobileOrFlutterApp && enableDynamicWebTwain && (
        <TwScanner
          isOpen={isTwScannerOpen}
          closeMenu={(): void => setIsTwScannerOpen(false)}
          onClose={closeScanner}
          onUploadSuccess={handleDocumentsUpload}
          orderId={loadData?.id || ''}
          tripId={segmentData.id || ''}
          defaultVisibility={isDriver ? true : isEnabledDriversSeeDocs}
          entityList={orderOptions}
          entity="trip"
          ltlEnabled={enableLtl}
          readableTripId={readableTripNumber}
          updateCounterTab={updateCounterExpenseTab}
        />
      )}

      {/** Document and photo preview */}
      {tripData &&
        tripData.trip_attachments &&
        (!isAdminOrDispatcher ||
          matchesMobileOrTablet ||
          !enableDocumentManager ||
          !selectedAttachment ||
          !selectedAttachment.documentAttachmentId) && (
          <TwOrderTripsDocViewer
            readableId={segmentData?.readable_id}
            selectedFileId={selectedDocumentId}
            files={filesToPreview}
            open={docViewOpen}
            onClickClose={(): void => {
              setDocViewOpen(false);
              setSelectedDocumentId('');
            }}
            onDelete={(doc: OrderAttachment): void => {
              setDocViewOpen(false);
              setSelectedDocument(doc);
              setShowDelete(true);
            }}
            entity="Trip"
          />
        )}

      {/* document viewer for admin and dispatcher */}
      {docViewOpen &&
        isAdminOrDispatcher &&
        selectedAttachment &&
        !!selectedAttachment.documentAttachmentId &&
        !matchesMobileOrTablet &&
        enableDocumentManager && (
          <TwPreviewDocument
            open={docViewOpen}
            files={filesToPreview.map((file) => {
              return {
                id: file.document_attachment_id,
                attachment_id: file.attachment_id,
                name: file.attachment.name,
                size: file.attachment.size,
                file_type: file.attachment.file_type,
                key: file.attachment.key,
                s3_key: file.attachment.s3_key,
                s3_signed_url: file.attachment.s3_signed_url,
                s3_signed_download_url: file.attachment.s3_signed_download_url,
                created_at: file.created_at
              } as AttachmentBasicProps;
            })}
            id={selectedAttachment.documentAttachmentId}
            isReview={false}
            onClose={(): void => setDocViewOpen(false)}
            reloadData={reloadData}
          />
        )}

      {(isDriver || isCarrier) && (
        <TwEditDateTime
          open={isEditDateTimeOpen}
          prevStopOutTime={editDateTimeConfig.prevStopOutTime}
          nextStopInTime={editDateTimeConfig.nextStopInTime}
          prevDateTime={editDateTimeConfig.prevDateTime}
          datetime={editDateTimeConfig.dateTime}
          nextDateTime={editDateTimeConfig.nextDateTime}
          title={editDateTimeConfig?.title}
          dateInputLabel={editDateTimeConfig?.dateInputLabel}
          onClose={(): void => {
            dispatch(closeEditDateTime());
          }}
          onSuccess={editDateTime}
        />
      )}
    </>
  );
};

export default TripTab;
